import type {
  StudyMissionSelectDto,
  StudyRunCaseDashboardThreadVerdictDto,
  StudySelectDto,
  StudyThreadSelectDto,
} from '@@types/api.types';
import {
  useGetMeiaRunCaseDashboard,
  useGetMeiaRunCases,
  useGetMeiaScoringOutputs,
  useGetMeiaStudyMissionMeasures,
  useGetMeiaStudyMissionThreads,
  useGetMeiaStudySignoffs,
  useGetMeiaStudyThreadSwimlanes,
  useGetMeiaStudyThreadTaskReadiness,
} from '@queries/meia.query';
import { useMemo } from 'react';
import type { MeiaPhaseKey } from '../meia.constants';
import { getMeiaMissionCode } from './meia-mission-display.util';
import { scoreFromBand } from './meia-p4-run-cases.util';
import { getDefaultBriefRunCaseId } from './meia-p5-brief.util';
import type { MeiaPhaseProgressMissionRow } from './meia-phase-progress-tile.component';
import { getMeiaMissionPhaseProgress, getMeiaStudyPhaseProgress } from './meia-phase-progress.util';

type UseMeiaStudyPhaseProgressParams = {
  missions: StudyMissionSelectDto[];
  study: StudySelectDto;
};

type UseMeiaStudyPhaseProgressResult = {
  isProgressError: boolean;
  isProgressLoading: boolean;
  isThreadsLoading: boolean;
  missionRows: MeiaPhaseProgressMissionRow[];
  studyProgressByPhase: Record<MeiaPhaseKey, number>;
  threads: StudyThreadSelectDto[];
};

const buildProgressByPhase = (compute: (phase: MeiaPhaseKey) => number): Record<MeiaPhaseKey, number> => ({
  p1: compute('p1'),
  p2: compute('p2'),
  p3: compute('p3'),
  p4: compute('p4'),
  p5: compute('p5'),
});

const verdictToScoreEntry = (verdict: StudyRunCaseDashboardThreadVerdictDto): Array<[string, number]> => {
  const score = verdict.score ?? scoreFromBand(verdict.band);
  return score === null ? [] : [[verdict.threadId, score]];
};

export const useMeiaStudyPhaseProgress = (params: UseMeiaStudyPhaseProgressParams): UseMeiaStudyPhaseProgressResult => {
  const { missions, study } = params;

  const {
    isError: isThreadsError,
    isLoading: isThreadsLoading,
    threads,
  } = useGetMeiaStudyMissionThreads({ missions, studyId: study.id });
  const {
    isError: isSwimlanesError,
    isLoading: isSwimlanesLoading,
    swimlanes,
  } = useGetMeiaStudyThreadSwimlanes({ studyId: study.id, threads });
  const {
    isError: isTaskReadinessError,
    isLoading: isTaskReadinessLoading,
    taskReadiness,
  } = useGetMeiaStudyThreadTaskReadiness({ studyId: study.id, threads });
  const {
    isError: isMeasuresError,
    isLoading: isMeasuresLoading,
    measureFlagsByMissionId,
  } = useGetMeiaStudyMissionMeasures({ missions });
  const { data: runCases = [], isError: isRunCasesError } = useGetMeiaRunCases({ studyId: study.id });
  const { data: scoringOutputs = [], isError: isScoringOutputsError } = useGetMeiaScoringOutputs({
    enabled: runCases.length > 0,
    studyId: study.id,
  });
  const dashboardRunCaseId = useMemo(
    () => getDefaultBriefRunCaseId({ runCases, scoringOutputs }),
    [runCases, scoringOutputs],
  );
  const { data: dashboard, isError: isDashboardError } = useGetMeiaRunCaseDashboard({
    enabled: Boolean(dashboardRunCaseId),
    runCaseId: dashboardRunCaseId ?? undefined,
    studyId: study.id,
  });
  const { data: signoffs = [], isError: isSignoffsError } = useGetMeiaStudySignoffs({ studyId: study.id });

  const threadScoresByThreadId = useMemo(
    () =>
      new Map((dashboard?.missions ?? []).flatMap((mission) => mission.threadVerdicts.flatMap(verdictToScoreEntry))),
    [dashboard?.missions],
  );

  const isStudySignedOff = useMemo(
    () => signoffs.some((signoff) => signoff.gate === 'G4' && !signoff.revokedAt),
    [signoffs],
  );

  const progressInputs = useMemo(
    () => ({
      isStudySignedOff,
      measureFlagsByMissionId,
      stages: swimlanes.map((swimlane) => ({ id: swimlane.id, threadId: swimlane.threadId })),
      taskRows: taskReadiness.map((row) => ({ swimlaneId: row.swimlaneId, threadId: row.threadId })),
      threadScoresByThreadId,
      threads: threads.map((thread) => ({ id: thread.id, missionId: thread.missionId })),
    }),
    [isStudySignedOff, measureFlagsByMissionId, swimlanes, taskReadiness, threadScoresByThreadId, threads],
  );

  const missionRows = useMemo<MeiaPhaseProgressMissionRow[]>(
    () =>
      missions.map((mission, index) => ({
        missionCode: getMeiaMissionCode({ index, mission }),
        missionId: mission.id,
        missionName: mission.name,
        progressByPhase: buildProgressByPhase((phase) =>
          getMeiaMissionPhaseProgress(phase, { ...progressInputs, mission }),
        ),
      })),
    [missions, progressInputs],
  );

  const studyProgressByPhase = useMemo(
    () => buildProgressByPhase((phase) => getMeiaStudyPhaseProgress(phase, { ...progressInputs, missions })),
    [missions, progressInputs],
  );

  const isProgressError =
    isDashboardError ||
    isMeasuresError ||
    isRunCasesError ||
    isScoringOutputsError ||
    isSignoffsError ||
    isSwimlanesError ||
    isTaskReadinessError ||
    isThreadsError;

  return {
    isProgressError,
    isProgressLoading: isMeasuresLoading || isSwimlanesLoading || isTaskReadinessLoading || isThreadsLoading,
    isThreadsLoading,
    missionRows,
    studyProgressByPhase,
    threads,
  };
};
